// src/pages/finance-portal/reports/BoardReports.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    FormControl, InputLabel, Select, MenuItem, CircularProgress, Stack, Card, CardContent,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, formatDate,
    firstOfYearIso, todayIso,
} from './reportShared';

export default function BoardReports() {
    const [reports, setReports] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [dialog, setDialog] = useState({ open: false });
    const [form, setForm] = useState({
        title: 'Board Package',
        template: 'board_deck',
        period_start: firstOfYearIso(),
        period_end: todayIso(),
        narrative: '',
    });

    const fetchReports = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/board-reports');
            if (res.data.success) setReports(res.data.data || []);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchReports(); }, [fetchReports]);

    const createReport = async () => {
        try {
            const res = await axiosInstance.post('/api/finance/board-reports', form);
            if (res.data.success) {
                setDialog({ open: false });
                setSelected(res.data.data);
                fetchReports();
            }
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to create');
        }
    };

    const approve = async (r) => {
        try {
            const res = await axiosInstance.post(`/api/finance/board-reports/${r.id}/approve`);
            if (res.data.success) {
                setSelected(res.data.data);
                fetchReports();
            }
        } catch (e) {
            alert(e.response?.data?.message || 'Failed');
        }
    };

    const deleteReport = async (r) => {
        if (!window.confirm(`Delete ${r.title}?`)) return;
        try {
            await axiosInstance.delete(`/api/finance/board-reports/${r.id}`);
            if (selected?.id === r.id) setSelected(null);
            fetchReports();
        } catch (e) {
            alert(e.response?.data?.message || 'Failed');
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <DescriptionIcon sx={{ color: ACCENT }} />
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Board Reports</Typography>
                </Stack>
                <Button startIcon={<AddIcon />} variant="contained"
                    sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}
                    onClick={() => setDialog({ open: true })}>New Report</Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                    <Paper>
                        {loading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Period</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reports.map((r) => (
                                        <TableRow key={r.id} hover
                                            selected={selected?.id === r.id}
                                            onClick={() => setSelected(r)}
                                            sx={{ cursor: 'pointer' }}>
                                            <TableCell>{r.title}</TableCell>
                                            <TableCell>{formatDate(r.period_start)} – {formatDate(r.period_end)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={r.status}
                                                    color={r.status === 'APPROVED' ? 'success' : 'default'} />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteReport(r); }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={7}>
                    {selected ? (
                        <Paper sx={{ p: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                    <Typography variant="h6">{selected.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatDate(selected.period_start)} – {formatDate(selected.period_end)} · {selected.template}
                                    </Typography>
                                </Box>
                                {selected.status !== 'APPROVED' && (
                                    <Button startIcon={<CheckCircleIcon />} color="success"
                                        onClick={() => approve(selected)}>Approve</Button>
                                )}
                            </Stack>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                {[
                                    ['Revenue', selected.auto_populated_data?.revenue],
                                    ['Expense', selected.auto_populated_data?.expense],
                                    ['Net Income', selected.auto_populated_data?.net_income],
                                    ['Total Assets', selected.auto_populated_data?.total_assets],
                                    ['Total Liabilities', selected.auto_populated_data?.total_liabilities],
                                    ['Book Equity', selected.auto_populated_data?.book_equity],
                                ].map(([label, val]) => (
                                    <Grid item xs={12} sm={6} md={4} key={label}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                    {formatCurrency(val)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                            {selected.narrative && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Narrative</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selected.narrative}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    ) : (
                        <Alert severity="info">Select a report to view details.</Alert>
                    )}
                </Grid>
            </Grid>

            <Dialog open={dialog.open} onClose={() => setDialog({ open: false })} maxWidth="sm" fullWidth>
                <DialogTitle>New Board Report</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField label="Title" fullWidth size="small"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Template</InputLabel>
                                <Select label="Template" value={form.template}
                                    onChange={(e) => setForm({ ...form, template: e.target.value })}>
                                    <MenuItem value="board_deck">Board Deck</MenuItem>
                                    <MenuItem value="investor_update">Investor Update</MenuItem>
                                    <MenuItem value="loan_covenant">Loan Covenant</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField label="Start" type="date" fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                                value={form.period_start}
                                onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField label="End" type="date" fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                                value={form.period_end}
                                onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Narrative" fullWidth multiline rows={4} size="small"
                                value={form.narrative}
                                onChange={(e) => setForm({ ...form, narrative: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog({ open: false })}>Cancel</Button>
                    <Button variant="contained" onClick={createReport}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
